<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import diningRoomWindows from './images/diningRoomWindows.jpg';
	import frontGate from './images/frontGate.jpg';
	import stateSealInstaller from './images/stateSealInstaller.jpg';
	import tinyWindowInstaller from './images/tinyWindowInstaller.jpg';
</script>

<Section>
	<h2 class="text-left">
		Preserving the Historic Oregon Governor's Mansion with Indow Inserts
	</h2>
	<p class="text-left">
		Mahonia Hall, the stately 1924 Tudor-style Oregon Governor's Mansion, is
		more than just a residence; it's a significant piece of Oregon's history,
		listed on the National Register of Historic Places. However, this grand old
		building faced a common problem for historic homes: severe drafts, so much
		so that it was once jokingly nicknamed "Pneumonia Hall".
	</p>
	<Image src={frontGate} loading="eager" />
	<p class="text-left">
		The original windows, while integral to the mansion's character, were also
		the source of significant heat loss and discomfort. For Governor John
		Kitzhaber and First Lady Cylvia Hayes, preserving the historic integrity of
		Mahonia Hall was paramount, making traditional window replacement an
		undesirable option. Their goal was to lead by example in energy efficiency,
		especially since the mansion frequently hosts out-of-state and international
		guests.
	</p>
	<Image src={diningRoomWindows} />

	<h2 class="text-left">
		The Solution: Innovative Energy Upgrades with Indow Inserts
	</h2>
	<p class="text-left">
		To tackle these challenges, the Governor and First Lady partnered with home
		performance experts Neil Kelly. Their comprehensive energy upgrade
		initiative included insulation, air sealing, LED lighting, and critically,
		the installation of 29 Indow window inserts.
	</p>

	<Image src={tinyWindowInstaller} />
	<p class="text-lg font-medium">
		Indow inserts provided an ideal solution for Mahonia Hall's historic
		windows.
	</p>
	<ul
		class="
    mb-12 grid list-none grid-cols-1 gap-6
    md:grid-cols-2
  "
	>
		<li>
			<h3 class="text-left">Preservation of Historic Integrity</h3>
			<p>
				Unlike replacement windows or exterior storm windows that can alter a
				building's appearance, Indow inserts are designed to preserve original
				windows. They do not require damaging tracks or magnetic systems.
			</p>
		</li>
		<li>
			<h3 class="text-left">Patented Compression Fit</h3>
			<p>
				Indow inserts rely on a patented silicone compression tube that simply
				presses into place within the existing window frame, ensuring a snug fit
				without permanent modifications. This method was highlighted by First
				Lady Hayes as an "innovative approach".
			</p>
		</li>
	</ul>

	<h2 class="text-left">
		Enhanced Comfort, Energy Savings, and Preserved Heritage
	</h2>
	<p class="text-left">
		The combination of energy upgrades, including the Indow inserts,
		dramatically transformed Mahonia Hall.
	</p>
	<p class="text-left">
		By choosing Indow inserts, the Oregon Governor's Mansion not only became a
		more comfortable and energy-efficient building but also set an example for
		how historic properties can embrace modern energy solutions while honoring
		their past.
	</p>

	<ul
		class="
    grid w-full list-none grid-cols-1 gap-6
    md:grid-cols-2
  "
	>
		<li>
			<Image src={stateSealInstaller} />
		</li>
		<li>
			<ul class="mb-12 list-none">
				<li>
					<h3 class="text-left">Increased Comfort</h3>
					<p>
						The inserts effectively eliminated the notorious drafts, making the
						mansion a much more comfortable space.
					</p>
				</li>
				<li>
					<h3 class="text-left">Significant Energy Savings</h3>
					<p>
						The improvements allowed the Governor and First Lady to keep the
						thermostat lower than before, leading to tangible energy savings.
						First Lady Hayes noted, "We can keep the thermostat lower than we
						could have a year ago with these improvements".
					</p>
				</li>
				<li>
					<h3 class="text-left">Historic Preservation</h3>
					<p>
						The most crucial outcome was the achievement of energy efficiency
						and comfort without compromising the historic integrity of this
						Oregon state treasure.
					</p>
				</li>
			</ul>
		</li>
	</ul>
</Section>

<GetAFreeEstimate />
